import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import { EFormBuilderFieldType, TFormBuilderDropdownField } from '@/types';

type TProps = {
  field: TFormBuilderDropdownField;
  index: number;
};

export const FormBuilderDropdown =
  /**
   * Dropdown field component for form builder with editable label, options and placeholder
   * @param {TProps} props - Component props
   * @param {TFormBuilderDropdownField} props.field - Dropdown field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder dropdown field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeDropdownFields = useCallback(
      (updated: Partial<TFormBuilderDropdownField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );

    const error =
      useFormBuilderFieldWithError<TFormBuilderDropdownField>(index);

    const optionsWithError = field.options.map((option, index) => ({
      ...option,
      error: !!error?.options?.[index],
    }));
    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.DROPDOWN}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
        error={!!error}
      >
        <FormBuilderElement
          alert={t('form-builder.dropdown-alert')}
          label={
            <FormBuilderElement.Label
              label={field.label}
              onLabelChange={(label) => changeDropdownFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) => changeDropdownFields({ required })}
          options={
            <FormBuilderElement.Options
              options={optionsWithError}
              onChange={(options) => changeDropdownFields({ options })}
            />
          }
        >
          <FormBuilderElement.Dropdown
            value={field.placeholder}
            onValueChange={(placeholder) =>
              changeDropdownFields({ placeholder })
            }
            error={!!error?.placeholder}
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderDropdown.displayName = 'FormBuilderDropdown';
